// frontend/src/pages/KybStatus.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiKybSummary } from "@/lib/api";
import type { KybStatusResponse, KybDoc } from "@/lib/api";
import KybDocBlock, { KybDocType } from "@/components/KybDocBlock";

const ORDER: KybDocType[] = [
  "certificate",
  "articles",
  "shareholders",
  "address_proof",
  "vat_certificate",
  "business_license",
];

function statusChip(status: KybStatusResponse["status"]) {
  switch (status) {
    case "approved":
      return "badge-success";
    case "pending":
      return "badge-warning";
    case "rejected":
      return "badge-danger";
    default:
      return "badge-gray";
  }
}

export default function KybStatus() {
  const [data, setData] = useState<KybStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await apiKybSummary();
        setData(snap);
      } catch (e: any) {
        setError(e?.message || "Failed to load KYB status.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const status = (data?.status ?? "not_started") as KybStatusResponse["status"];
  const kyb = data?.kyb || null;

  // Latest document per type (if backend returns multiple per type)
  const latestByType: Partial<Record<KybDocType, KybDoc | undefined>> =
    useMemo(() => {
      const bucket: Record<KybDocType, KybDoc[]> = {
        certificate: [],
        articles: [],
        shareholders: [],
        address_proof: [],
        vat_certificate: [],
        business_license: [],
      };
      (kyb?.documents || []).forEach((d) => {
        const t = (d.type as KybDocType) || "certificate";
        if (bucket[t]) bucket[t].push(d);
      });
      const pickLatest = (arr: KybDoc[]) =>
        arr.sort((a, b) => {
          const da = new Date((a as any).created_at || (a as any).updated_at || "").getTime() || 0;
          const db = new Date((b as any).created_at || (b as any).updated_at || "").getTime() || 0;
          return db - da;
        })[0];
      return {
        certificate: pickLatest(bucket.certificate),
        articles: pickLatest(bucket.articles),
        shareholders: pickLatest(bucket.shareholders),
        address_proof: pickLatest(bucket.address_proof),
        vat_certificate: pickLatest(bucket.vat_certificate),
        business_license: pickLatest(bucket.business_license),
      };
    }, [kyb]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6 pb-28 md:pb-6 space-y-4">
        <div className="section-box skeleton h-10 rounded-xl" />
        <div className="card-box skeleton h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-4 sm:p-6 pb-28 md:pb-6">
      {/* Global status header */}
      <div className="card-box">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Know Your Business (KYB)</h3>
          <span className={`chip ${statusChip(status)}`}>
            {String(status).toUpperCase()}
          </span>
        </div>

        {/* Blade-style banners */}
        {status === "approved" && (
          <div className="mt-3 alert-success">
            Your KYB has been <strong>approved</strong>.
          </div>
        )}
        {status === "pending" && (
          <div className="mt-3 alert-info">
            Your KYB is <strong>pending</strong> review.
          </div>
        )}
        {status === "rejected" && (
          <div className="mt-3 alert-danger flex items-center justify-between">
            <span>
              Your KYB was <strong>rejected</strong>. See document details
              below.
            </span>
            {!!kyb?.id && (
              <Link
                to={`/kyb/${kyb.id}/edit`}
                className="text-blue-700 hover:underline"
              >
                Resubmit
              </Link>
            )}
          </div>
        )}
        {error && <div className="mt-3 alert-danger">{error}</div>}
      </div>

      {/* Documents list (VIEW-ONLY on status page) */}
      <div className="card-box">
        <h3 className="text-lg font-semibold mb-4">Documents</h3>

        {ORDER.map((t) => (
          <KybDocBlock
            key={t}
            docType={t}
            doc={latestByType[t] || (undefined as any)}
            allowUpload={false}
          />
        ))}

        {/* If no KYB yet, show helper text similar to Blade */}
        {status === "not_started" && (
          <div className="mt-3 text-sm text-gray-600">
            You haven’t submitted any KYB documents yet.
          </div>
        )}
      </div>

      {/* Footer CTA (Blade parity) */}
      <div className="flex items-center gap-3 mb-24 md:mb-0">
        {status === "not_started" && (
          <Link to="/kyb/create" className="btn btn-success">
            Start KYB
          </Link>
        )}

        {kyb?.id && status !== "not_started" && (
          <>
            {status === "approved" ? (
              <button
                type="button"
                className="btn btn-white opacity-60 cursor-not-allowed"
                disabled
                title="KYB is approved"
              >
                Open Resubmission Page
              </button>
            ) : (
              <Link to={`/kyb/${kyb.id}/edit`} className="btn">
                Open Resubmission Page
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
